// CreateJobFormSteps.tsx
import React, { useState, useEffect } from 'react';
import {
  Accordion, AccordionItem, AccordionHeader, AccordionBody, Input, Label, TabContent, TabPane
} from 'reactstrap';
import { FileUpdateResult } from './FileUpload';
import CustomGoogleAutoComplete from './CustomGoogleAutoComplete';
import { JobDescriptionForm } from './JobDescriptionForm';
import FileUpload from './FileUpload';
import { parseAddress, Address, formatAddress } from '../../models/Address.interface';
import { ChatMessageProps } from './ChatMessage';
import { Attachment } from '../../models/Attachment.interface';
import { uploadJobAttachmentsToFirebase } from './JobSubmissionService';
import { CONTACT_PERSON_ROLES, formatContactPersonRoleOption, getContactPersonRoleByValue, getDefaultContactPersonRole } from '../../models/ContactPersonRole.interface';

interface FormData {
  jobDescription: string;
  setJobDescription: React.Dispatch<React.SetStateAction<string>>;
  jobAttachments: File[];
  setJobAttachments: React.Dispatch<React.SetStateAction<File[]>>;
  existingAttachments: Attachment[];
  setExistingAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>;
  jobTitle: string;
  setJobTitle: React.Dispatch<React.SetStateAction<string>>;
  jobSummary: string;
  setJobSummary: React.Dispatch<React.SetStateAction<string>>;
  seoUrl: string;
  setSeoUrl: React.Dispatch<React.SetStateAction<string>>;
  jobDetail: string;
  setJobDetail: React.Dispatch<React.SetStateAction<string>>;
  jobCategories: string[];
  setJobCategories: React.Dispatch<React.SetStateAction<string[]>>;
  complete: boolean;
  setComplete: React.Dispatch<React.SetStateAction<boolean>>;
  chatMessages: ChatMessageProps[];
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessageProps[]>>;
  jobStatus: string;
  setJobStatus: React.Dispatch<React.SetStateAction<string>>;
  ownerName: string;
  setOwnerName: React.Dispatch<React.SetStateAction<string>>;
  ownerEmail: string;
  setOwnerEmail: React.Dispatch<React.SetStateAction<string>>;
  ownerPhone: string;
  setOwnerPhone: React.Dispatch<React.SetStateAction<string>>;
  contactPersonRole: string;
  setContactPersonRole: React.Dispatch<React.SetStateAction<string>>;
  propertyAddress: Address;
  setPropertyAddress: React.Dispatch<React.SetStateAction<Address>>;
}

interface CreateJobFormStepsProps {
  formData: FormData;
  activeTab: number;
  onTabChange: (tab: number) => void;
  jobId?: string | null;
  onFileUpload?: (attachments: Attachment[]) => void;
}

export const CreateJobFormSteps: React.FC<CreateJobFormStepsProps> = ({
  formData,
  activeTab,
  onTabChange,
  jobId,
  onFileUpload
}) => {
  const [openAccordion, setOpenAccordion] = useState(['1', '2']);
  const [fileUpdates, setFileUpdates] = useState<FileUpdateResult>({
    newFiles: [],
    deletedUrls: [],
    attachmentOrder: [],
    captions: {}
  });
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  // Preloaded addresses (could be fetched from a service)
  const preloadedAddresses: string[] = [];

  // Set default contactPersonRole if not already set
  useEffect(() => {
    if (!formData.contactPersonRole) {
      formData.setContactPersonRole(getDefaultContactPersonRole());
    }
  }, [formData.contactPersonRole, formData.setContactPersonRole]);

  // Development environment localStorage clear button
  const isDevelopment = process.env.NODE_ENV === 'development';

  const clearCreateJobLocalStorage = () => {
    const keys = Object.keys(localStorage).filter(key => key.startsWith('createJob_'));
    keys.forEach(key => localStorage.removeItem(key));
    alert(`Cleared ${keys.length} localStorage items that begin with 'createJob_'`);
    // Optionally reload the page to reset the form
    window.location.reload();
  };

  const toggleAccordion = (id: string) => {
    setOpenAccordion(prev =>
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const handleAddressSelect = (address: string) => {
    formData.setPropertyAddress(parseAddress(address));
  };

  const handleExistingFileDelete = (url: string) => {
    //console.log("🔵 [CreateJobFormSteps] handleExistingFileDelete:", url);

    // Remove from existing attachments
    formData.setExistingAttachments(prev => {
      const filtered = prev.filter(attachment => attachment.url !== url);
      //console.log("🔵 [CreateJobFormSteps] After delete - attachments count:", filtered.length);
      return filtered;
    });
  };

  const handleFileUpdates = async (updates: FileUpdateResult) => {
    //console.log("🔵 [CreateJobFormSteps] handleFileUpdates received:", updates);

    // Update local file state
    setFileUpdates(prev => ({
      newFiles: updates.newFiles || prev.newFiles,
      deletedUrls: Array.from(new Set([...prev.deletedUrls, ...(updates.deletedUrls || [])])),
      attachmentOrder: updates.attachmentOrder || prev.attachmentOrder,
      captions: {
        ...prev.captions,
        ...updates.captions
      }
    }));

    // Only handle uploads if we have new files, a jobId, and we're not already uploading
    if (updates.newFiles && updates.newFiles.length > 0 && jobId && !isUploadingFiles) {
      setIsUploadingFiles(true);
      try {
        //console.log('🔵 [CreateJobFormSteps] Starting upload for files:', updates.newFiles.map(f => f.name));
        const uploadedAttachments = await uploadJobAttachmentsToFirebase(updates.newFiles, jobId);

        //console.log('🔵 [CreateJobFormSteps] Upload completed, received attachments:', uploadedAttachments.length);

        // Add uploaded attachments to existing attachments with strict deduplication
        if (uploadedAttachments && uploadedAttachments.length > 0) {
          formData.setExistingAttachments(prev => {
            //console.log("🔵 [CreateJobFormSteps] Before adding uploads - existing count:", prev.length);

            // Create a Set of existing URLs for fast lookup
            const existingUrls = new Set(prev.map(att => att.url));

            // Only add attachments that don't already exist
            const newAttachments = uploadedAttachments.filter(att => {
              if (existingUrls.has(att.url)) {
                //console.log("🟡 [CreateJobFormSteps] Skipping duplicate attachment:", att.url);
                return false;
              }
              return true;
            });

            if (newAttachments.length > 0) {
              //console.log("🔵 [CreateJobFormSteps] Adding new attachments:", newAttachments.length);
              const combined = [...prev, ...newAttachments];
              //console.log("🔵 [CreateJobFormSteps] After adding uploads - final count:", combined.length);
              return combined;
            } else {
              //console.log("🟡 [CreateJobFormSteps] No new attachments to add (all were duplicates)");
              return prev;
            }
          });

          // Clear the new files since they're now uploaded
          formData.setJobAttachments([]);

          // Don't call onFileUpload callback - let the parent handle existingAttachments state changes
        }

        //console.log('🔵 [CreateJobFormSteps] Files uploaded successfully');
      } catch (error) {
        console.error('🔴 [CreateJobFormSteps] Error uploading files:', error);
        // Handle upload error - could show a toast notification
      } finally {
        setIsUploadingFiles(false);
      }
    }
  };

  const handleTabChangeWithValidation = (targetTab: number) => {
    // Validate Step 1 before moving to Step 2
    if (activeTab === 7 && targetTab === 8) {
      const isStep1Valid = formData.ownerName &&
        formData.ownerEmail &&
        formData.propertyAddress.address1 &&
        formData.contactPersonRole;

      if (!isStep1Valid) {
        alert('Please fill in all required fields in Step 1 before proceeding.');
        return;
      }
    }

    onTabChange(targetTab);
  };

  //console.log("🔵 [CreateJobFormSteps] Rendering FileUpload with attachments:", formData.existingAttachments.length);

  return (
    <>
      <TabContent activeTab={activeTab}>
        {/* Tab 1: Contact Info & Job Address */}
        <TabPane tabId={7}>
          <div>
            <h5>Contact Info & Job Address</h5>
            <p className="text-muted">Please provide your contact information and the address of the job</p>
          </div>

          <div className="mb-3">
            <Label for="ownerName">Full Name <span className="text-danger">*</span></Label>
            <Input
              type="text"
              id="ownerName"
              value={formData.ownerName}
              onChange={(e) => formData.setOwnerName(e.target.value)}
              required
            />
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <Label for="ownerEmail">Email Address <span className="text-danger">*</span></Label>
                <Input
                  type="email"
                  id="ownerEmail"
                  value={formData.ownerEmail}
                  onChange={(e) => formData.setOwnerEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <Label for="ownerPhone">Phone Number <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  id="ownerPhone"
                  value={formData.ownerPhone}
                  onChange={(e) => formData.setOwnerPhone(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="mb-3">
            <Label>Job Address <span className="text-danger">*</span></Label>
            <CustomGoogleAutoComplete
              preloadedAddresses={preloadedAddresses}
              onAddressSelect={handleAddressSelect}
              initialValue={formatAddress(formData.propertyAddress)}
            />
          </div>

          <div className="mb-3">
            <Label for="contactPersonRole">What is your role in this hiring process? <span className="text-danger">*</span></Label>
            <Input
              type="select"
              id="contactPersonRole"
              value={formData.contactPersonRole}
              onChange={(e) => formData.setContactPersonRole(e.target.value)}
              required
              style={{ padding: "0.69rem 2.3rem 0.69rem 0.92rem" }}
            >
              <option value="">Select your role...</option>
              {CONTACT_PERSON_ROLES.map((role) => (
                <option key={role.role} value={role.role}>
                  {formatContactPersonRoleOption(role)}
                </option>
              ))}
            </Input>
            {formData.contactPersonRole && (
              <div className="mt-2">
                <small className="text-muted">
                  Selected: {getContactPersonRoleByValue(formData.contactPersonRole)?.displayName}
                </small>
              </div>
            )}
          </div>
          <div className="d-flex align-items-start gap-3 mt-4">
            <button
              type="button"
              className="btn btn-success btn-label right ms-auto nexttab nexttab"
              onClick={() => handleTabChangeWithValidation(8)}
              disabled={!formData.ownerName || !formData.ownerEmail || !formData.propertyAddress.address1 || !formData.contactPersonRole}
            >
              <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
              Go to Job Details
            </button>
          </div>
        </TabPane>

        {/* Tab 2: Job Details */}
        <TabPane tabId={8}>
          <div>
            <Accordion open={openAccordion} toggle={toggleAccordion}>
              <AccordionItem>
                <AccordionHeader targetId="1">Project Submission Process</AccordionHeader>
                <AccordionBody accordionId="1">
                  <ul>
                    <li>Submit your project details</li>
                    <li>BidBro AI will review your job description</li>
                    <li>We'll prompt you for additional information to:
                      <ul>
                        <li>Ensure a complete job description</li>
                        <li>Increase contractor interest</li>
                        <li>Enable quoting without on-site visits</li>
                      </ul>
                    </li>
                    <li>If you lack requested information:
                      <ul>
                        <li>Provide as much detail as possible</li>
                        <li>Click "Mark Complete" to proceed</li>
                      </ul>
                    </li>
                    <li>Note: Contractors may still ask questions before bidding</li>
                  </ul>
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="2">Enter Project Details</AccordionHeader>
                <AccordionBody accordionId="2">
                  <JobDescriptionForm
                    jobDescription={formData.jobDescription}
                    setJobDescription={formData.setJobDescription}
                    jobTitle={formData.jobTitle}
                    setJobTitle={formData.setJobTitle}
                    jobSummary={formData.jobSummary}
                    setJobSummary={formData.setJobSummary}
                    jobDetail={formData.jobDetail}
                    setJobDetail={formData.setJobDetail}
                    jobCategories={formData.jobCategories}
                    setJobCategories={formData.setJobCategories}
                    complete={formData.complete}
                    setComplete={formData.setComplete}
                    chatMessages={formData.chatMessages}
                    setChatMessages={formData.setChatMessages}
                    seoUrl={formData.seoUrl}
                    setSeoUrl={formData.setSeoUrl}
                  />
                </AccordionBody>
              </AccordionItem>
            </Accordion>
          </div>
          <div>

          </div>
          <div className="d-flex align-items-start gap-3 mt-4">
            <button
              type="button"
              className="btn btn-light btn-label previestab"
              onClick={() => handleTabChangeWithValidation(activeTab - 1)}
              style={{ paddingLeft: "45px" }}
            >
              <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2"></i>{" "}
              <span className="d-none d-md-inline">Back to Project Address</span>
              <span className="d-inline d-md-none">Project Address</span>

            </button>
            <button
              type="button"
              className="btn btn-success btn-label right ms-auto nexttab"
              onClick={() => handleTabChangeWithValidation(activeTab + 1)}
              disabled={!formData.complete}
            >
              <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
              <span className="d-none d-md-inline">Go to Images / Attachments</span>
              <span className="d-inline d-md-none">Images / Attachments</span>
            </button>
          </div>
          {!formData.complete && (
            <div className="alert alert-warning mt-3">
              <small>
                <i className="ri-information-line me-1"></i>
                Complete your job description above to continue to the next step.
              </small>
            </div>
          )}
        </TabPane>

        {/* Tab 3: Images / Attachments */}
        <TabPane tabId={9}>
          <div>
            <h5>Images / Attachments</h5>
            <p className="text-muted">
              Upload project plans, current pictures, designs, and more. Include enough pictures and attachments to help contractors quote your job.
            </p>
            {isUploadingFiles && (
              <div className="alert alert-info">
                <i className="ri-upload-line me-2"></i>
                Uploading files...
              </div>
            )}
          </div>

          <div className="mb-3">
            <Label for="attachments">Attachments</Label>
            <FileUpload
              onFilesChange={(files) => {
                //console.log("🔵 [CreateJobFormSteps] onFilesChange called with:", files.length, "files");
                formData.setJobAttachments(files);
                handleFileUpdates({
                  ...fileUpdates,
                  newFiles: files
                });
              }}
              existingFiles={formData.existingAttachments}
              onExistingFileDelete={handleExistingFileDelete}
              onAttachmentUpdate={handleFileUpdates}
              jobId={jobId || undefined}
            />
          </div>

          <div className="d-flex align-items-start gap-3 mt-4">
            <button
              type="button"
              className="btn btn-light btn-label previestab"
              onClick={() => onTabChange(8)}
              style={{
                paddingLeft: "45px"
              }}
            >
              <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2"></i>
              Back to Job Details
            </button>
            <button
              type="button"
              className="btn btn-success btn-label right ms-auto nexttab"
              onClick={() => onTabChange(10)}
              disabled={!formData.jobTitle || !formData.jobSummary || formData.jobCategories.length === 0}
            >
              <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
              Review & Submit
            </button>
          </div>
        </TabPane>

        {/* Tab 4: Confirm Details */}
        <TabPane tabId={10}>
          <div>
            <div className="mb-3">
              <h5>Job Title</h5>
              <Input
                type="text"
                value={formData.jobTitle}
                onChange={(e) => formData.setJobTitle(e.target.value)}
                className="mb-3"
              />
            </div>

            <div className="mb-3">
              <h5>Job Summary</h5>
              <Input
                type="textarea"
                value={formData.jobSummary}
                onChange={(e) => formData.setJobSummary(e.target.value)}
                rows={4}
                className="mb-3"
              />
            </div>

            <div className="mb-3">
              <h6>Job Details</h6>
              <Input
                type="textarea"
                value={formData.jobDetail}
                onChange={(e) => formData.setJobDetail(e.target.value)}
                rows={6}
                className="mb-3"
              />
            </div>

            <div className="mb-3">
              <h6>Project Address</h6>
              <p className="text-muted">
                {formData.propertyAddress.address1}, {formData.propertyAddress.city}, {formData.propertyAddress.state} {formData.propertyAddress.zipCode}
              </p>
            </div>

            <div className="mb-3">
              <h6>Contact Information</h6>
              <p className="text-muted">
                <strong>Name:</strong> {formData.ownerName}<br />
                <strong>Email:</strong> {formData.ownerEmail}<br />
                {formData.ownerPhone && (
                  <>
                    <strong>Phone:</strong> {formData.ownerPhone}<br />
                  </>
                )}
                {formData.contactPersonRole && (
                  <>
                    <strong>Role:</strong> {getContactPersonRoleByValue(formData.contactPersonRole)?.displayName}<br />
                  </>
                )}
              </p>
            </div>

            <div className="mb-3">
              <h6>Job Categories</h6>
              <div>
                {formData.jobCategories.map((category, index) => (
                  <span key={index} className="badge bg-primary me-2 mb-1" style={{whiteSpace: "wrap"}}>
                    {category}
                  </span>
                ))}
                {formData.jobCategories.length === 0 && (
                  <span className="text-muted">No categories selected</span>
                )}
              </div>
            </div>

            <div className="mb-3">
              <h6>Attachments</h6>
              {formData.existingAttachments.length > 0 ? (
                <ul className="list-group">
                  {formData.existingAttachments.map((attachment, index) => (
                    <li key={index} className="list-group-item">
                      <i className="ri-file-line me-2"></i>
                      <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                        Attachment {index + 1}
                      </a>
                      {attachment.caption && (
                        <small className="text-muted ms-2">- {attachment.caption}</small>
                      )}
                    </li>
                  ))}
                </ul>
              ) : formData.jobAttachments.length > 0 ? (
                <ul className="list-group">
                  {formData.jobAttachments.map((file, index) => (
                    <li key={index} className="list-group-item">
                      <i className="ri-file-line me-2"></i>
                      {file.name} ({(file.size / 1024).toFixed(2)} KB)
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted">No attachments uploaded</p>
              )}
            </div>

            <div className="mb-3">
              <h6>Job Status</h6>
              <div className="d-flex align-items-center">
                {formData.complete ? (
                  <span className="badge bg-success">
                    <i className="ri-check-line me-1"></i>Ready to Submit
                  </span>
                ) : (
                  <span className="badge bg-warning">
                    <i className="ri-time-line me-1"></i>In Progress
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="d-flex align-items-start gap-3 mt-4">
            <button
              type="button"
              className="btn btn-light btn-label previestab"
              onClick={() => onTabChange(9)}
              style={{
                minWidth: "120px",
                paddingLeft: "45px"
              }}
            >
              <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2"></i>
              Back to Images / Attachments
            </button>
          </div>
        </TabPane>
      </TabContent>

      {/* Development environment localStorage clear button */}
      {isDevelopment && (
        <button
          type="button"
          onClick={clearCreateJobLocalStorage}
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            zIndex: 1050,
            fontSize: '12px',
            padding: '8px 12px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
          title="Clear all localStorage items that begin with 'createJob_'"
        >
          🗑️ Clear createJob_*
        </button>
      )}
    </>
  );
};